import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { toast } from 'sonner';

const CartContext = createContext();

// Custom event for cart updates (used by PusherSessionTracker)
const CART_UPDATE_EVENT = 'godmode-cart-update';

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within CartProvider');
  }
  return context;
};

// Sanitize a cart item to ensure only serializable fields are stored.
// Module-level so it can be used inside useState's lazy initializer.
const sanitizeCartItem = (item) => {
  if (!item || typeof item !== 'object') return null;
  const moqRaw = item.moq;
  const moq = typeof moqRaw === 'number' && moqRaw > 0 ? moqRaw : (parseInt(moqRaw, 10) > 0 ? parseInt(moqRaw, 10) : 1);
  return {
    jp_part_no: item.jp_part_no || '',
    part_name: item.part_name || '',
    mrp: typeof item.mrp === 'number' ? item.mrp : 0,
    discounted_price: typeof item.discounted_price === 'number' ? item.discounted_price : item.mrp,
    discount_percentage: typeof item.discount_percentage === 'number' ? item.discount_percentage : 0,
    oe_part_no: item.oe_part_no || '',
    category: item.category || '',
    image: item.image || '',
    brand: item.brand || '',
    has_special_price: item.has_special_price || false,
    moq,
    coupon_amount: typeof item.coupon_amount === 'number' ? item.coupon_amount : (parseInt(item.coupon_amount, 10) || 0),
    quantity: typeof item.quantity === 'number' && item.quantity > 0 ? item.quantity : 1,
  };
};

// Read & sanitize the persisted cart synchronously during render so the
// initial React state already matches localStorage. This eliminates the
// race where a post-mount save-effect would clobber an externally-seeded
// cart (e.g. AuthContext.startImpersonation's live_cart hand-off).
const readPersistedCart = () => {
  try {
    const saved = localStorage.getItem('sparePartsCart');
    if (!saved) return [];
    const parsed = JSON.parse(saved);
    if (!Array.isArray(parsed)) return [];
    return parsed.map(sanitizeCartItem).filter(i => i !== null && i.jp_part_no);
  } catch (_) {
    return [];
  }
};

export const CartProvider = ({ children }) => {
  // Lazy-init from localStorage so cart === persisted value on first render
  // (prevents the save-effect from writing '[]' over a freshly-seeded cart).
  const [cart, setCart] = useState(readPersistedCart);
  const cartUpdateTimeoutRef = useRef(null);
  
  // Broadcast cart update for God Mode tracking
  const broadcastCartUpdate = useCallback((updatedCart) => {
    // Debounce rapid updates (50ms for faster response)
    if (cartUpdateTimeoutRef.current) {
      clearTimeout(cartUpdateTimeoutRef.current);
    }
    cartUpdateTimeoutRef.current = setTimeout(() => {
      console.log('[GodMode Cart] 🛒 Broadcasting cart update:', {
        itemCount: updatedCart.length,
        items: updatedCart.map(i => ({ part: i.jp_part_no, qty: i.quantity }))
      });
      
      const event = new CustomEvent(CART_UPDATE_EVENT, {
        detail: {
          cart: updatedCart,
          timestamp: Date.now()
        }
      });
      window.dispatchEvent(event);
    }, 50);
  }, []);

  // Save cart to localStorage whenever it changes. Lazy-init ensures the
  // very first save writes the same value that was read in, so there's no
  // clobber of an externally-seeded cart (impersonation hand-off).
  useEffect(() => {
    try {
      localStorage.setItem('sparePartsCart', JSON.stringify(cart));
    } catch (error) {
      console.error('Error saving cart to localStorage:', error);
      // If quota exceeded, try to clear old data
      if (error.name === 'QuotaExceededError') {
        console.warn('localStorage quota exceeded, cart not saved');
      }
    }
  }, [cart]);

  // Snap quantity up to the nearest multiple of MOQ (and at least MOQ).
  // MOQ-aware everywhere so a customer can never check out with a sub-MOQ
  // quantity even if they hand-edit localStorage.
  const snapToMoq = (qty, moq) => {
    const m = Math.max(1, parseInt(moq || 1, 10));
    const q = Math.max(1, parseInt(qty || 1, 10));
    if (q < m) return m;
    if (q % m === 0) return q;
    return Math.ceil(q / m) * m;
  };

  const addToCart = (part, quantity = 1) => {
    try {
      // Use the shared sanitization function
      const sanitizedPart = sanitizeCartItem({ ...part, quantity });

      if (!sanitizedPart || !sanitizedPart.jp_part_no) {
        console.error('Invalid part data, cannot add to cart:', part);
        toast.error('Invalid product data');
        return;
      }

      // Enforce MOQ at the cart boundary. If the caller passed a qty below
      // MOQ or not a multiple, snap it up so the user can never end up
      // with an invalid quantity.
      const moq = sanitizedPart.moq || 1;
      const snapped = snapToMoq(sanitizedPart.quantity, moq);
      if (snapped !== sanitizedPart.quantity) {
        toast.warning(`Min order is ${moq} pcs. Quantity adjusted to ${snapped}.`);
        sanitizedPart.quantity = snapped;
      }

      setCart(prevCart => {
        // Also sanitize existing cart items during add operation as an extra safety measure
        const safeCart = prevCart.map(item => sanitizeCartItem(item)).filter(item => item !== null);

        const existingItem = safeCart.find(item => item.jp_part_no === sanitizedPart.jp_part_no);
        let newCart;

        if (existingItem) {
          const combinedQty = snapToMoq(existingItem.quantity + sanitizedPart.quantity, moq);
          toast.success('Updated cart quantity');
          newCart = safeCart.map(item =>
            item.jp_part_no === sanitizedPart.jp_part_no
              ? { ...item, quantity: combinedQty, moq, coupon_amount: sanitizedPart.coupon_amount }
              : item
          );
        } else {
          toast.success('Added to cart');
          newCart = [...safeCart, sanitizedPart];
        }

        // Broadcast immediately for God Mode
        broadcastCartUpdate(newCart);
        return newCart;
      });
    } catch (error) {
      console.error('Error adding item to cart:', error);
      toast.error('Failed to add item to cart. Please try again.');
    }
  };

  const removeFromCart = (jp_part_no) => {
    setCart(prevCart => {
      const newCart = prevCart.filter(item => item.jp_part_no !== jp_part_no);
      // Broadcast immediately for God Mode
      broadcastCartUpdate(newCart);
      return newCart;
    });
    toast.success('Removed from cart');
  };

  const updateQuantity = (jp_part_no, quantity) => {
    if (quantity <= 0) {
      removeFromCart(jp_part_no);
      return;
    }
    setCart(prevCart => {
      const newCart = prevCart.map(item => {
        if (item.jp_part_no !== jp_part_no) return item;
        const moq = item.moq || 1;
        const snapped = snapToMoq(quantity, moq);
        if (snapped !== quantity) {
          // The cart UI calls updateQuantity with the new target; if the
          // target isn't on a MOQ boundary we silently snap it. The +/-
          // controls already step by MOQ so this branch is mostly a
          // safety net for direct callers.
          toast.warning(`Quantity snapped to a multiple of MOQ ${moq}`);
        }
        return { ...item, quantity: snapped };
      });
      broadcastCartUpdate(newCart);
      return newCart;
    });
  };

  const clearCart = () => {
    setCart([]);
    localStorage.removeItem('sparePartsCart');
    // Broadcast empty cart for God Mode
    broadcastCartUpdate([]);
  };

  const getTotalItems = () => {
    return cart.reduce((total, item) => total + item.quantity, 0);
  };

  const getTotalMRP = () => {
    return cart.reduce((total, item) => total + (item.mrp * item.quantity), 0);
  };

  const getTotalDiscountedPrice = () => {
    return cart.reduce((total, item) => {
      const price = item.discounted_price || item.mrp;
      return total + (price * item.quantity);
    }, 0);
  };

  const getTotalSavings = () => {
    return getTotalMRP() - getTotalDiscountedPrice();
  };

  // Legacy function for backward compatibility
  const getTotalPrice = () => {
    return getTotalDiscountedPrice();
  };

  return (
    <CartContext.Provider
      value={{
        cart,
        addToCart,
        removeFromCart,
        updateQuantity,
        clearCart,
        getTotalItems,
        getTotalPrice,
        getTotalMRP,
        getTotalDiscountedPrice,
        getTotalSavings,
      }}
    >
      {children}
    </CartContext.Provider>
  );
};
